import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import { searchOpsAccounts } from '../../../requests';
import { TOpsClassDetailEx } from '../../../requests/opsRegistrationsExtra';
import { btnGhost, Card, labelClass } from '../ui';
import { fieldClass, TClassForm, TSetField } from './shared';

type Props = {
  form: TClassForm;
  set: TSetField;
  isNew: boolean;
  existing?: TOpsClassDetailEx;
  accountId: string;
  accountName: string;
  setAccountId: (v: string) => void;
  setAccountName: (v: string) => void;
};

/** Primary and on-site contacts, and the account search. */
const ContactsSection: React.FC<Props> = ({
  form,
  set,
  isNew,
  existing,
  accountId,
  accountName,
  setAccountId,
  setAccountName,
}) => {
  const [accountQuery, setAccountQuery] = useState('');
  const { data: accountHits = [] } = useQuery({
    queryKey: ['opsAccounts', accountQuery],
    queryFn: () => searchOpsAccounts(accountQuery),
    enabled: accountQuery.trim().length >= 2,
  });

  return (
    <Card title="Class Contacts">
      <div className="p-5">
        {!isNew && existing?.primaryContact && (
          <div className="mb-3 text-sm">
            <div className={labelClass}>Primary contact</div>
            <Link
              className="font-medium text-gray-four hover:text-blue-primary hover:underline"
              to={PATH.opsContactEdit.replace(
                ':id',
                existing.primaryContact.id,
              )}
            >
              {existing.primaryContact.name ?? existing.primaryContact.id}
            </Link>
            <span className="ml-2 text-xs text-gray-bold">
              {[existing.primaryContact.email, existing.primaryContact.phone]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
        )}
        <div className="mb-3">
          <label className={labelClass}>On-site contact</label>
          <input
            className={fieldClass}
            placeholder="Name and phone of who meets the instructor on site"
            value={form.onsiteContact}
            onChange={(e) => set('onsiteContact')(e.target.value)}
          />
        </div>
        <label className={labelClass}>Account</label>
        {accountId ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="rounded-full bg-blue-100 px-3 py-1 font-medium text-blue-primary">
              {accountName}
            </span>
            <button
              type="button"
              className={btnGhost}
              onClick={() => {
                setAccountId('');
                setAccountName('');
                setAccountQuery('');
              }}
            >
              change
            </button>
          </div>
        ) : (
          <>
            <input
              className={fieldClass}
              placeholder="Type at least 2 characters to search accounts…"
              value={accountQuery}
              onChange={(e) => setAccountQuery(e.target.value)}
            />
            {accountHits.length > 0 && (
              <div className="mt-1 overflow-hidden rounded-lg border border-white-regular bg-white shadow-light">
                {accountHits.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className="block w-full px-3 py-2 text-left text-sm transition-colors hover:bg-blue-100/60"
                    onClick={() => {
                      setAccountId(a.id);
                      setAccountName(a.name);
                    }}
                  >
                    {a.name}
                    {(a.type || a.city) && (
                      <span className="text-xs text-gray-bold">
                        {' · ' + [a.type, a.city].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
};

export default ContactsSection;
